/**
 * useNotebooksCrud (Intelligence Studio)
 *
 * SRP hook: notebook list CRUD + delete confirmation state transitions.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 */

export default function useNotebooksCrud({
  intelligenceStudioApi,
  notebooks,
  selectedNotebook,
  deleteConfirmation,
  setIsLoading,
  setNotebooks,
  setSelectedNotebook,
  setView,
  setSearchParams,
  setError,
  setHasLoadedOnce,
  setDeleteConfirmation,
}) {
  const fetchNotebooks = async () => {
    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
    try {
      setIsLoading(true);
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await intelligenceStudioApi.listNotebooks({
            limit: 30,
            offset: 0,
            includeFiles: false,
          });
          const data = response?.data || response;
          setNotebooks(data.data || data || []);
          setError?.(null);
          return;
        } catch (err) {
          lastError = err;
          if (attempt < 1) {
            await sleep(700);
          }
        }
      }
      console.error('Failed to fetch notebooks:', lastError);
      setError('Unable to load notebooks right now. Please retry.');
    } catch (err) {
      console.error('Failed to fetch notebooks:', err);
      setError('Unable to load notebooks right now. Please retry.');
    } finally {
      setIsLoading(false);
      if (typeof setHasLoadedOnce === 'function') {
        setHasLoadedOnce(true);
      }
    }
  };

  // Open delete confirmation modal
  const openDeleteConfirmation = notebook => {
    setDeleteConfirmation({
      open: true,
      notebookId: notebook.id,
      notebookName: notebook.name || notebook.title || 'Untitled Notebook',
    });
  };

  // Close delete confirmation modal
  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({ open: false, notebookId: null, notebookName: '' });
  };

  // Confirm delete action
  const confirmDelete = async () => {
    const id = deleteConfirmation.notebookId;
    if (!id) return;

    try {
      await intelligenceStudioApi.deleteNotebook(id);
      setNotebooks(prev => prev.filter(n => n.id !== id));
      if (selectedNotebook?.id === id) {
        setSelectedNotebook(null);
        setView('home');
        setSearchParams({}); // Clear URL
      }
      closeDeleteConfirmation();
    } catch (err) {
      console.error('Failed to delete notebook:', err);
      closeDeleteConfirmation();
    }
  };

  const handleDeleteNotebook = async notebookOrId => {
    // If passed an object, use it directly; if passed an ID, find the notebook
    const notebook =
      typeof notebookOrId === 'object'
        ? notebookOrId
        : notebooks.find(n => n.id === notebookOrId) || { id: notebookOrId };
    openDeleteConfirmation(notebook);
  };

  // ✅ NEW: Rename notebook handler
  const handleRenameNotebook = async (notebookId, newTitle) => {
    if (!newTitle?.trim()) return;

    try {
      await intelligenceStudioApi.updateNotebook(notebookId, newTitle.trim());

      // Update local state
      setNotebooks(prev =>
        prev.map(n => (n.id === notebookId ? { ...n, title: newTitle.trim() } : n))
      );

      if (selectedNotebook?.id === notebookId) {
        setSelectedNotebook(prev => ({ ...prev, title: newTitle.trim() }));
      }

      console.log('✅ Notebook renamed:', newTitle);
    } catch (err) {
      console.error('Failed to rename notebook:', err);
      setError('Failed to rename notebook');
    }
  };

  // Legacy function for direct delete (keeping for backward compatibility)
  const handleDeleteNotebookDirect = async id => {
    try {
      await intelligenceStudioApi.deleteNotebook(id);
      setNotebooks(prev => prev.filter(n => n.id !== id));
      if (selectedNotebook?.id === id) setSelectedNotebook(null);
    } catch (err) {
      setError('Failed to delete notebook');
    }
  };

  return {
    fetchNotebooks,
    openDeleteConfirmation,
    closeDeleteConfirmation,
    confirmDelete,
    handleDeleteNotebook,
    handleRenameNotebook,
    handleDeleteNotebookDirect,
  };
}
